import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Star, Trash2, FileDown, FileText, Calendar, Clock, Loader2, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, hasFeature } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { subjectsOf, fmtDate, fmtDuration, SOURCE_LABEL } from "@/lib/constants";
import { SubjectBadge } from "@/components/app/CourseCard";
import { NotesTab } from "@/components/course/NotesTab";
import { RevisionTab } from "@/components/course/RevisionTab";
import { QuizTab } from "@/components/course/QuizTab";
import { AskTab } from "@/components/course/AskTab";
import { HighlightedTranscript } from "@/components/course/Markdown";

const TitleEditor = ({ c, setCourse }) => {
  const { user } = useAuth();
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(c.title);
  const [subject, setSubject] = useState(c.subject);
  const save = async () => {
    try { const { data } = await api.patch(`/courses/${c.course_id}`, { title, subject }); setCourse(data); setEdit(false); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!edit) return (
    <div className="flex items-start gap-3">
      <h1 data-testid="course-title" className="text-3xl font-extrabold tracking-tight sm:text-4xl">{c.title || "Cours en préparation"}</h1>
      <button data-testid="edit-title-button" onClick={() => { setTitle(c.title); setSubject(c.subject); setEdit(true); }} className="mt-2 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><Pencil className="h-4 w-4" /></button>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input data-testid="edit-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="h-11 min-w-[240px] flex-1 rounded-xl border bg-card px-4 text-lg font-bold outline-none focus:border-primary/50" />
      <Select value={subject} onValueChange={setSubject}>
        <SelectTrigger data-testid="edit-subject-select" className="h-11 w-48 rounded-xl bg-card"><SelectValue /></SelectTrigger>
        <SelectContent>{subjectsOf(user).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <button data-testid="save-title-button" onClick={save} className="bg-brand inline-flex h-11 w-11 items-center justify-center rounded-full text-white"><Check className="h-4 w-4" /></button>
    </div>
  );
};

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/courses/${id}`); setCourse(data); } catch (e) { if (e?.response?.status === 404) setNotFound(true); else toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const busy = c && (c.status === "processing" || c.revision_status === "processing" || c.quiz_status === "processing");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(load, 3500);
    return () => clearInterval(t);
  }, [busy, load]);

  if (notFound) return <div className="card-flat p-10 text-center" data-testid="course-not-found">Ce cours n'existe pas ou a été supprimé. <Link to="/app" className="font-semibold text-primary">Retour</Link></div>;
  if (!c) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const toggleFav = async () => { const { data } = await api.patch(`/courses/${id}`, { favorite: !c.favorite }); setCourse(data); };
  const remove = async () => {
    try { await api.delete(`/courses/${id}`); toast.success("Cours supprimé définitivement."); navigate("/app"); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportMd = async () => {
    try {
      const res = await api.get(`/courses/${id}/export/markdown`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${(c.title || "cours").replace(/[^\p{L}\p{N}_-]+/gu, "-")}.md`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const exportPdf = () => (hasFeature(user, "pdf") ? window.open(`/app/imprimer/${id}?type=notes`, "_blank") : toast.info("L'export PDF est inclus dans les offres Premium et Pro."));

  return (
    <div data-testid="course-detail-page">
      <Link to="/app" data-testid="back-to-dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Mes cours</Link>
      <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <SubjectBadge subject={c.subject} />
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(c.created_at)}</span>
            {c.duration_sec > 0 && <span className="inline-flex items-center gap-1" data-testid="course-duration"><Clock className="h-3.5 w-3.5" /> {fmtDuration(c.duration_sec)}</span>}
            <span>{SOURCE_LABEL[c.source]}</span>
          </div>
          <TitleEditor key={c.title + c.subject} c={c} setCourse={setCourse} />
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="course-favorite-button" onClick={toggleFav} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold hover:border-amber-400/60">
            <Star className={`h-4 w-4 ${c.favorite ? "fill-amber-400 text-amber-400" : ""}`} /> {c.favorite ? "Favori" : "Ajouter aux favoris"}
          </button>
          <button data-testid="export-markdown-button" disabled={c.status !== "ready"} onClick={exportMd} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold hover:border-primary/40 disabled:opacity-50"><FileText className="h-4 w-4" /> Markdown</button>
          <button data-testid="export-pdf-button" disabled={c.status !== "ready"} onClick={exportPdf} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold hover:border-primary/40 disabled:opacity-50"><FileDown className="h-4 w-4" /> PDF</button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-course-button" className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold text-destructive hover:border-destructive/50"><Trash2 className="h-4 w-4" /> Supprimer</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer ce cours ?</AlertDialogTitle>
                <AlertDialogDescription>Les notes, la transcription, la fiche, le quiz et les questions seront supprimés définitivement.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-course-cancel" className="rounded-full">Annuler</AlertDialogCancel>
                <AlertDialogAction data-testid="delete-course-confirm" onClick={remove} className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90">Supprimer définitivement</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Tabs defaultValue="notes" className="mt-8">
        <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-full bg-muted/70 p-1">
          {[["notes", "Notes"], ["fiche", "Fiche de révision"], ["quiz", "Quiz"], ["questions", "Questions"], ["transcription", "Transcription"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`course-tab-${v}`} className="rounded-full px-4 py-1.5 data-[state=active]:shadow-sm">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="notes" className="mt-6"><NotesTab c={c} setCourse={setCourse} reload={load} /></TabsContent>
        <TabsContent value="fiche" className="mt-6"><RevisionTab c={c} reload={load} /></TabsContent>
        <TabsContent value="quiz" className="mt-6"><QuizTab c={c} reload={load} /></TabsContent>
        <TabsContent value="questions" className="mt-6">{c.status === "ready" ? <AskTab c={c} /> : <p className="text-sm text-muted-foreground">Disponible dès que les notes sont prêtes.</p>}</TabsContent>
        <TabsContent value="transcription" className="mt-6">
          <div className="card-flat p-6 sm:p-8" data-testid="course-transcript">
            <p className="eyebrow mb-4">{c.source === "texte" ? "Notes collées" : "Transcription brute"}</p>
            {c.transcript ? <HighlightedTranscript text={c.transcript} /> : <p className="text-sm text-muted-foreground">Aucune transcription pour l'instant.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
